import React, { useCallback } from "react";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { setAppLicenseBannerDismissedFor } from "src/pages/Studies/states/studiesSlice";
import { APP_LICENSE_EXPIRY_FALLBACK_TEMPLATE, composeAppLicenseMessage, formatRemaining, useAppLicenseStatus } from "./useAppLicenseStatus";
import "./style.css";

/**
 * Full-width "toaster" pinned to the top of the app during the app-license-expiry countdown window
 * (from 30 days before expiry through the 1-day post-expiry grace). APIs still return 200 in this
 * window, so this is a warning, not a lock. It is always shown in the theme red. Dismissing it
 * records the (expiry + message) key in Redux (persisted via redux-persist), so it stays hidden
 * across reloads for this window and collapses into the navbar avatar indicator (see TopNavbar) —
 * but resurfaces if the backend pushes a new expiry time or message. Once the v2 cutoff passes, the
 * backend's 403 takes over via the full-page splash.
 */
const AppLicenseExpiryBanner: React.FC = () => {
	const { t } = useTranslation();
	const dispatch = useDispatch();
	const { dismissKey, isDismissed, messageTemplate, msRemaining, status } = useAppLicenseStatus();

	// Dismiss against the (expiry + message) key so the banner returns if the backend later pushes a
	// new expiry time or a changed message — never just the expiry date.
	const handleDismiss = useCallback(() => {
		dispatch(setAppLicenseBannerDismissedFor(dismissKey));
	}, [dispatch, dismissKey]);

	if (status !== "expiring" || isDismissed) return null;

	const duration = formatRemaining(msRemaining);
	const text = composeAppLicenseMessage(messageTemplate, t(duration.key, duration.vars), t(APP_LICENSE_EXPIRY_FALLBACK_TEMPLATE));

	return (
		<div className="app-license-banner-host" role="alert" aria-live="assertive">
			<div className="app-license-banner">
				<i className="material-symbols-outlined app-license-banner__icon" aria-hidden="true">
					info
				</i>
				<span className="app-license-banner__text">{text}</span>
				<button type="button" className="app-license-banner__close" onClick={handleDismiss} aria-label={t("Dismiss")}>
					<i className="material-symbols-outlined">close</i>
				</button>
			</div>
		</div>
	);
};

export default React.memo(AppLicenseExpiryBanner);
